import { Box, Stack, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';

import { DocumentCard } from '@/components/DocumentCard';
import { Button } from '@/components/form/baseInputs/Button';
import { Loader } from '@/components/progress/Loader/Loader';
import { useIsHouseholdWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsHouseholdWorkflowTrackerInProgress';
import { useOnWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnWorkflowTrackerComplete';
import { WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { useDocumentsWithProcessingJobsQuery } from './graphql/DocumentProcessingStatus.generated';
import { useReviewDocumentSummary } from './ReviewDocumentSummaryTrowser/hooks/useReviewDocumentSummary';
import { ReviewDocumentSummaryTrowser } from './ReviewDocumentSummaryTrowser/ReviewDocumentSummaryTrowser';

export interface DocumentProcessingStatusProps {
  /**
   * The household ID to fetch document jobs for
   */
  householdId: string;
  /**
   * The main heading text (h1)
   */
  header: string;
  /**
   * The subheading text (subtitle1)
   */
  subheader: ReactNode;
  /**
   * Callback when all documents are processed
   */
  onAllProcessed?: () => void;
  /**
   * The workflow tracker kind to monitor for progress
   */
  workflowTrackerKind: WorkflowTrackerKind;
  /**
   * The document IDs that were uploaded
   */
  uploadedDocumentIds: string[];
}

export function DocumentProcessingStatus({
  householdId,
  header,
  subheader,
  workflowTrackerKind,
  uploadedDocumentIds,
  onAllProcessed,
}: DocumentProcessingStatusProps) {
  const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
    null
  );
  const { document: documentSummary, loading: isLoadingDocumentSummary } =
    useReviewDocumentSummary(loadingDocumentId || '', !loadingDocumentId);

  const isDocumentProcessingInProgress =
    useIsHouseholdWorkflowTrackerInProgress(workflowTrackerKind);

  const {
    data,
    loading: loadingDocuments,
    refetch,
  } = useDocumentsWithProcessingJobsQuery({
    variables: {
      householdId,
      documentIds: uploadedDocumentIds,
    },
    skip: !uploadedDocumentIds.length,
  });

  const documents = getNodes(data?.documents);

  // Track completion of document processing for this household
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasProcessingStarted, setHasProcessingStarted] = useState(false);

  useEffect(() => {
    // Initialize tracking when we first detect processing should be happening
    if (!hasInitialized && uploadedDocumentIds.length && householdId) {
      diagnostics.info(
        '[DocumentProcessingStatus] Initializing document processing tracking',
        {
          householdId,
          workflowTrackerKind,
          uploadedDocumentIds,
        }
      );
      setHasInitialized(true);
    }
  }, [hasInitialized, householdId, workflowTrackerKind, uploadedDocumentIds]);

  useEffect(() => {
    if (
      isDocumentProcessingInProgress &&
      hasInitialized &&
      !hasProcessingStarted
    ) {
      setHasProcessingStarted(true);
    }
  }, [isDocumentProcessingInProgress, hasInitialized, hasProcessingStarted]);

  // Watch for document extraction completion
  useOnWorkflowTrackerComplete({
    kind: workflowTrackerKind,
    onCompleted: () => {
      diagnostics.info(
        '[DocumentProcessingStatus] Document processing completed',
        {
          householdId,
          jobKind: workflowTrackerKind,
        }
      );
      void refetch();
      onAllProcessed?.();
    },
  });

  const isComplete =
    !isDocumentProcessingInProgress && hasInitialized && hasProcessingStarted;

  if (loadingDocuments || !documents.length) {
    return (
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="title-xl">{header}</Typography>
          <Typography variant="body-xs">{subheader}</Typography>
        </Stack>
        <Box display="flex" justifyContent="center" p={4}>
          <Loader />
        </Box>
      </Stack>
    );
  }

  if (!documents.length) {
    diagnostics.warn('No documents found', {
      householdId,
      documentIds: uploadedDocumentIds,
      jobKind: workflowTrackerKind,
    });
    return null;
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="title-xl">{header}</Typography>
          <Typography variant="body-xs">{subheader}</Typography>
        </Stack>

        <Stack spacing={2}>
          {documents.map((document) => {
            return (
              <DocumentCard
                key={document.id}
                filename={document.name}
                status={isComplete ? 'complete' : 'progress'}
                controls={
                  isComplete &&
                  workflowTrackerKind ===
                    WorkflowTrackerKind.DocumentSummary && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setLoadingDocumentId(document.id)}
                      loading={
                        loadingDocumentId === document.id &&
                        isLoadingDocumentSummary
                      }
                    >
                      Review summary
                    </Button>
                  )
                }
              />
            );
          })}
        </Stack>
      </Stack>
      {documentSummary && loadingDocumentId && (
        <ReviewDocumentSummaryTrowser
          isOpen
          onClose={() => setLoadingDocumentId(null)}
          documentSummary={documentSummary}
        />
      )}
    </>
  );
}
